import { TONE_OKLCH } from '@frisk/shared';
import type { ItemWithExpiry } from '../api';
import { t } from '../theme';
import { useStore } from '../store';

/**
 * The best-before date of one batch, editable in place.
 *
 * The date *is* the control rather than sitting behind an edit button: a
 * native date input opens the iPad's own wheel picker, which beats anything
 * drawn here, and there is no mode to enter or leave.
 *
 * Per batch on purpose — a group holds several dates, so "the" date of a
 * product is not a thing you can edit.
 */
export function BestBeforeField({ batch, compact = false }: { batch: ItemWithExpiry; compact?: boolean }) {
  const setBestBefore = useStore((s) => s.setBestBefore);
  const flash = useStore((s) => s.flash);

  const commit = async (value: string) => {
    const next = value || null;
    if (next === batch.bestBefore) return;
    try {
      await setBestBefore(batch.id, next);
      flash(next ? 'Haltbarkeit geändert' : 'Datum entfernt');
    } catch {
      flash('Konnte nicht geändert werden');
    }
  };

  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: compact ? '4px 10px' : '8px 14px',
        borderRadius: 12,
        background: 'oklch(0.2 0.012 60)',
        border: t.hairline,
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          flex: 'none',
          borderRadius: 999,
          background: TONE_OKLCH[batch.expiry.tone],
        }}
      />
      <input
        type="date"
        value={batch.bestBefore ?? ''}
        onChange={(e) => void commit(e.target.value)}
        style={{
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: batch.bestBefore ? t.ink : t.inkFaint,
          fontFamily: t.mono,
          fontSize: compact ? 14 : 16,
          padding: 0,
          minWidth: 0,
          // Safari renders an empty date input as blank rather than a hint.
          width: compact ? 118 : 132,
        }}
      />
    </label>
  );
}
